/**
 * Typed PostHog analytics wrapper.
 * All business-critical events go through here so we have a single source of truth
 * for event names — useful for PostHog insights, cohorts, and the weekly UX report.
 */
import posthog from 'posthog-js';

export type LandingRegisterSurface =
  | 'hero-primary'
  | 'hero-quick-start'
  | 'signup-readiness';

export type RegisterPrefillSource = 'landing-quick-start' | 'unknown';

// ─── Auth events ────────────────────────────────────────────────────────────

export const trackUserRegistered = (userId: string, email: string) => {
  posthog.capture('user_registered', { user_id: userId, email });
};

export const trackUserLoggedIn = (userId: string, method: 'email' | 'sso' | 'mfa' = 'email') => {
  posthog.capture('user_logged_in', { user_id: userId, method });
};

/**
 * Fires on every page load for authenticated users (session restore via stored token).
 * This is the most reliable custom event — it fires regardless of which pages or
 * features the user visits, giving us a guaranteed active-user signal in PostHog.
 */
export const trackSessionRestored = (userId: string) => {
  posthog.capture('session_restored', { user_id: userId });
};

// ─── Onboarding ──────────────────────────────────────────────────────────────

export const trackOnboardingStarted = () => {
  posthog.capture('onboarding_started');
};

export const trackOnboardingCompleted = (walletCount: number) => {
  posthog.capture('onboarding_completed', { wallet_count: walletCount });
};

// ─── Wallet / portfolio ───────────────────────────────────────────────────────

export const trackWalletAdded = (chain: string) => {
  posthog.capture('wallet_added', { chain });
};

export const trackWalletRemoved = () => {
  posthog.capture('wallet_removed');
};

export const trackPortfolioViewed = (walletCount: number) => {
  posthog.capture('portfolio_viewed', { wallet_count: walletCount });
};

export const trackPortfolioImported = (source: 'csv' | 'exchange' | 'wallet') => {
  posthog.capture('portfolio_imported', { source });
};

// ─── Risk & DeFi ─────────────────────────────────────────────────────────────

export const trackRiskAnalyzed = (riskScore: number) => {
  posthog.capture('risk_analyzed', { risk_score: riskScore });
};

export const trackDeFiHealthChecked = (protocolCount: number) => {
  posthog.capture('defi_health_checked', { protocol_count: protocolCount });
};

// ─── Alerts ───────────────────────────────────────────────────────────────────

export const trackAlertCreated = (alertType: string) => {
  posthog.capture('alert_created', { alert_type: alertType });
};

export const trackAlertTriggered = (alertType: string) => {
  posthog.capture('alert_triggered', { alert_type: alertType });
};

// ─── Tax ─────────────────────────────────────────────────────────────────────

export const trackTaxReportGenerated = () => {
  posthog.capture('tax_report_generated');
};

// ─── Billing / upgrade ───────────────────────────────────────────────────────

export const trackUpgradeClicked = (fromPage: string, plan: string) => {
  posthog.capture('upgrade_clicked', { from_page: fromPage, target_plan: plan });
};

export const trackSubscriptionStarted = (plan: string) => {
  posthog.capture('subscription_started', { plan });
};

// ─── Public landing / acquisition ───────────────────────────────────────────

export const trackLandingRegisterIntent = (
  surface: LandingRegisterSurface,
  prefillsEmail: boolean
) => {
  posthog.capture('landing_register_intent', {
    surface,
    prefills_email: prefillsEmail,
  });
};

export const trackRegisterPrefillViewed = (
  source: RegisterPrefillSource,
  hasValidEmail: boolean
) => {
  posthog.capture('register_prefill_viewed', {
    source,
    has_valid_email: hasValidEmail,
  });
};

// ─── General feature usage ───────────────────────────────────────────────────

export const trackFeatureUsed = (featureName: string, properties?: Record<string, unknown>) => {
  posthog.capture('feature_used', { feature: featureName, ...properties });
};
